import { FC, useState } from 'react';
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { EnumCutsheetQuestionnaireQuestionsType } from 'lib/graphql';

/** Union of all types selectable from the add-question menu */
export type AddableQuestionType =
  | 'global-spec'
  | EnumCutsheetQuestionnaireQuestionsType.FreeSelection
  | EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup
  | EnumCutsheetQuestionnaireQuestionsType.TrimRanking
  | EnumCutsheetQuestionnaireQuestionsType.TrimPercentage
  | EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection
  | EnumCutsheetQuestionnaireQuestionsType.InfoSlide;

interface AddQuestionButtonProps {
  /** Zero-based index at which a newly created question will be inserted */
  insertAtIndex: number;
  /**
   * Called when the user picks a question type from the menu.
   * The parent is responsible for opening the creation form modal.
   */
  onSelectType: (type: AddableQuestionType, insertAtIndex: number) => void;
}

/**
 * Subtle insertion-point button placed between (or around) questions.
 * @param root0
 * @param root0.insertAtIndex
 * @param root0.onSelectType
 */
export const AddQuestionButton: FC<AddQuestionButtonProps> = ({
  insertAtIndex,
  onSelectType,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (type: AddableQuestionType) => {
    handleClose();
    onSelectType(type, insertAtIndex);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        opacity: 0.7,
        transition: 'opacity 0.2s ease-in-out, padding 0.2s ease-in-out',
        '&:hover': { opacity: 1, py: 3 },
      }}
    >
      {/* Left divider arm */}
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />

      <Button
        size="small"
        variant="text"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{
          mx: 1.5,
          borderStyle: 'dashed',
          minWidth: 0,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          transition: 'opacity 0.2s, transform 0.2s',
          '&:hover': {
            transform: 'scale(1.05) translateY(-2px)',
          },
        }}
      >
        Add Question
      </Button>

      {/* Right divider arm */}
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleSelect('global-spec')}>
          <Stack>
            <Typography variant="inherit">Specification</Typography>
            <Typography variant="caption" color="text.secondary">
              Ask a specification as a standalone question
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleSelect(EnumCutsheetQuestionnaireQuestionsType.FreeSelection)
          }
        >
          <Stack>
            <Typography variant="inherit">Multi-Choice Cuts</Typography>
            <Typography variant="caption" color="text.secondary">
              Customer picks any cuts from a list
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleSelect(EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup)
          }
        >
          <Stack>
            <Typography variant="inherit">Either/Or Cuts</Typography>
            <Typography variant="caption" color="text.secondary">
              Customer picks one option; each option bundles a set of cuts
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleSelect(EnumCutsheetQuestionnaireQuestionsType.TrimRanking)
          }
        >
          <Stack>
            <Typography variant="inherit">Trim by Weight</Typography>
            <Typography variant="caption" color="text.secondary">
              Customer chooses trims and specifies pounds for each
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleSelect(EnumCutsheetQuestionnaireQuestionsType.TrimPercentage)
          }
        >
          <Stack>
            <Typography variant="inherit">Trim by Percentage</Typography>
            <Typography variant="caption" color="text.secondary">
              Customer allocates percentages across trims (totals 100%)
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleSelect(EnumCutsheetQuestionnaireQuestionsType.InfoSlide)
          }
        >
          <Stack>
            <Typography variant="inherit">Information Slide</Typography>
            <Typography variant="caption" color="text.secondary">
              Informational content with title, details, and image
            </Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </Box>
  );
};
